import { useCallback, useEffect, useState } from 'react'
import {
  Clock,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Package,
  X,
  History,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type {
  RevisionRule,
  RevisionSchedule,
  AdminERP,
  AdminCustomer,
  EquipmentItem,
  EquipmentRevision,
} from '../../types'
import { formatDate } from '../../utils/helpers'

type Tab = 'rules' | 'schedules' | 'parc'

function StatusBadge({ status }: { status: RevisionSchedule['status'] }) {
  const { t } = useTranslation()
  if (status === 'overdue')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <AlertCircle size={11} />
        {t('revisions.statusOverdue')}
      </span>
    )
  if (status === 'due')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <AlertTriangle size={11} />
        {t('revisions.statusDue')}
      </span>
    )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle size={11} />
      {t('revisions.statusOk')}
    </span>
  )
}

function EquipmentStatusBadge({ item }: { item: EquipmentItem }) {
  const { t } = useTranslation()
  if (item.status === 'end_of_life') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        {t('parc.statusEndOfLife')}
      </span>
    )
  }
  if (item.status === 'retired') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
        {t('parc.statusRetired')}
      </span>
    )
  }
  if (item.next_revision_at) {
    const diff = (new Date(item.next_revision_at).getTime() - Date.now()) / 86400000
    if (diff < 0)
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle size={11} />
          {t('revisions.statusOverdue')}
        </span>
      )
    if (diff <= 30)
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertTriangle size={11} />
          {t('revisions.statusDue')}
        </span>
      )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle size={11} />
      {t('revisions.statusOk')}
    </span>
  )
}

const emptyRule: Partial<RevisionRule> = {
  product_ref: '',
  label: '',
  interval_days: 365,
  is_active: true,
  erp_id: 0,
}

const emptyEquipment: Partial<EquipmentItem> = {
  rule_id: 0,
  supplier_id: 0,
  serial_number: '',
  purchase_date: '',
  notes: '',
  status: 'active',
}

export default function AdminRevisions() {
  const { t } = useTranslation()
  const isSuperAdmin = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')?.role === 'super_admin'
    } catch {
      return false
    }
  })()
  const [tab, setTab] = useState<Tab>('rules')

  // Rules tab state
  const [rules, setRules] = useState<RevisionRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(true)
  const [erps, setErps] = useState<AdminERP[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editRule, setEditRule] = useState<RevisionRule | null>(null)
  const [formData, setFormData] = useState<Partial<RevisionRule>>(emptyRule)
  const [formSaving, setFormSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Schedules tab state
  const [schedules, setSchedules] = useState<RevisionSchedule[]>([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState<string | null>(null)

  // Parc tab state
  const [parc, setParc] = useState<EquipmentItem[]>([])
  const [parcLoading, setParcLoading] = useState(false)
  const [parcStatusFilter, setParcStatusFilter] = useState('')
  const [parcSupplierFilter, setParcSupplierFilter] = useState('')
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [showEquipForm, setShowEquipForm] = useState(false)
  const [editEquip, setEditEquip] = useState<EquipmentItem | null>(null)
  const [equipFormData, setEquipFormData] = useState<Partial<EquipmentItem>>(emptyEquipment)
  const [equipFormSaving, setEquipFormSaving] = useState(false)
  const [equipFormError, setEquipFormError] = useState<string | null>(null)

  // Revise modal (admin parc)
  const [reviseItem, setReviseItem] = useState<EquipmentItem | null>(null)
  const [reviseDate, setReviseDate] = useState('')
  const [reviseNotes, setReviseNotes] = useState('')
  const [reviseSaving, setReviseSaving] = useState(false)
  const [reviseError, setReviseError] = useState<string | null>(null)

  // History modal
  const [historyItem, setHistoryItem] = useState<EquipmentItem | null>(null)
  const [history, setHistory] = useState<EquipmentRevision[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Load ERPs and customers once
  useEffect(() => {
    api
      .getAdminERPs()
      .then(setErps)
      .catch(() => setErps([]))
    api
      .getAdminCustomers()
      .then(setCustomers)
      .catch(() => setCustomers([]))
  }, [])

  const loadRules = useCallback(async () => {
    setRulesLoading(true)
    try {
      const data = await api.getAdminRevisionRules()
      setRules(data)
    } finally {
      setRulesLoading(false)
    }
  }, [])

  const loadSchedules = useCallback(async () => {
    setSchedulesLoading(true)
    try {
      const params: Record<string, string> = {}
      if (statusFilter) params.status = statusFilter
      const data = await api.getAdminRevisions(params)
      setSchedules(data)
    } finally {
      setSchedulesLoading(false)
    }
  }, [statusFilter])

  const loadParc = useCallback(async () => {
    setParcLoading(true)
    try {
      const params: Record<string, string> = {}
      if (parcStatusFilter) params.status = parcStatusFilter
      if (parcSupplierFilter) params.supplier_id = parcSupplierFilter
      const data = await api.getAdminParc(params)
      setParc(data)
    } finally {
      setParcLoading(false)
    }
  }, [parcStatusFilter, parcSupplierFilter])

  useEffect(() => {
    if (tab === 'rules') loadRules()
    else if (tab === 'schedules') loadSchedules()
    else loadParc()
  }, [tab, loadRules, loadSchedules, loadParc])

  const handleSync = async () => {
    setSyncing(true)
    setSyncMsg(null)
    try {
      const res = await api.syncAdminRevisions()
      setSyncMsg(t('adminRevisions.syncSuccess', { count: res.synced }))
      await loadSchedules()
    } catch {
      setSyncMsg(t('adminRevisions.syncError'))
    } finally {
      setSyncing(false)
    }
  }

  const openAdd = () => {
    setEditRule(null)
    setFormData({ ...emptyRule, erp_id: erps[0]?.id ?? 0 })
    setFormError(null)
    setShowForm(true)
  }

  const openEdit = (rule: RevisionRule) => {
    setEditRule(rule)
    setFormData({ ...rule })
    setFormError(null)
    setShowForm(true)
  }

  const handleDelete = async (rule: RevisionRule) => {
    if (!window.confirm(t('adminRevisions.deleteConfirm'))) return
    try {
      await api.deleteAdminRevisionRule(rule.id)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      alert(apiErr?.response?.data?.error || 'Erreur suppression')
    }
  }

  const handleSaveRule = async () => {
    if (!formData.product_ref || !formData.label || !formData.erp_id) {
      setFormError('Référence produit, libellé et ERP sont requis')
      return
    }
    setFormSaving(true)
    setFormError(null)
    try {
      if (editRule) {
        const updated = await api.updateAdminRevisionRule(editRule.id, formData)
        setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      } else {
        const created = await api.createAdminRevisionRule(formData)
        setRules((prev) => [...prev, created])
      }
      setShowForm(false)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setFormError(apiErr?.response?.data?.error || 'Erreur sauvegarde')
    } finally {
      setFormSaving(false)
    }
  }

  // Equipment CRUD
  const openAddEquip = () => {
    setEditEquip(null)
    setEquipFormData({
      ...emptyEquipment,
      rule_id: rules[0]?.id ?? 0,
      supplier_id: customers[0]?.id ?? 0,
    })
    setEquipFormError(null)
    setShowEquipForm(true)
  }

  const openEditEquip = (item: EquipmentItem) => {
    setEditEquip(item)
    setEquipFormData({
      rule_id: item.rule_id,
      supplier_id: item.supplier_id,
      serial_number: item.serial_number ?? '',
      label: item.label ?? '',
      purchase_date: item.purchase_date ?? '',
      status: item.status,
      notes: item.notes ?? '',
    })
    setEquipFormError(null)
    setShowEquipForm(true)
  }

  const handleSaveEquip = async () => {
    if (!equipFormData.rule_id || !equipFormData.supplier_id) {
      setEquipFormError(t('parc.ruleAndCustomerRequired'))
      return
    }
    setEquipFormSaving(true)
    setEquipFormError(null)
    try {
      if (editEquip) {
        const updated = await api.updateAdminParcItem(editEquip.id, equipFormData)
        setParc((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))
      } else {
        const created = await api.createAdminParcItem(equipFormData)
        setParc((prev) => [...prev, created])
      }
      setShowEquipForm(false)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setEquipFormError(apiErr?.response?.data?.error || 'Erreur sauvegarde')
    } finally {
      setEquipFormSaving(false)
    }
  }

  const handleDeleteEquip = async (item: EquipmentItem) => {
    if (!window.confirm(t('adminRevisions.deleteConfirm'))) return
    try {
      await api.deleteAdminParcItem(item.id)
      setParc((prev) => prev.filter((i) => i.id !== item.id))
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      alert(apiErr?.response?.data?.error || 'Erreur suppression')
    }
  }

  const openRevise = (item: EquipmentItem) => {
    setReviseItem(item)
    setReviseDate(new Date().toISOString().split('T')[0])
    setReviseNotes('')
    setReviseError(null)
  }

  const handleRevise = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reviseItem || !reviseDate) return
    setReviseSaving(true)
    setReviseError(null)
    try {
      const updated = await api.reviseAdminEquipmentItem(
        reviseItem.id,
        reviseDate,
        reviseNotes || undefined
      )
      setParc((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))
      setReviseItem(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setReviseError(apiErr?.response?.data?.error || t('parc.reviseError'))
    } finally {
      setReviseSaving(false)
    }
  }

  const openHistory = async (item: EquipmentItem) => {
    setHistoryItem(item)
    setHistory([])
    setHistoryLoading(true)
    try {
      const data = await api.getAdminParcItemHistory(item.id)
      setHistory(data)
    } catch {
      // show empty
    } finally {
      setHistoryLoading(false)
    }
  }

  const tabDefs: { key: Tab; label: string }[] = [
    { key: 'rules', label: t('adminRevisions.tabRules') },
    { key: 'schedules', label: t('adminRevisions.tabSchedules') },
    { key: 'parc', label: t('parc.tabParc') },
  ]

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="h-6 w-6 text-blue-600" />
          {t('adminRevisions.title')}
        </h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {tabDefs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Rules tab ── */}
      {tab === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus size={16} />
              {t('adminRevisions.addRule')}
            </button>
          </div>

          {rulesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Clock className="mx-auto h-10 w-10 mb-3 opacity-40" />
              <p>{t('adminRevisions.noRules')}</p>
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 bg-white dark:bg-gray-800 rounded-xl shadow sm:hidden">
                {rules.map((rule) => (
                  <li key={rule.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                          {rule.label}
                        </p>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-0.5">
                          {rule.product_ref}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${rule.is_active ? 'bg-green-500' : 'bg-gray-400'}`}
                        />
                        <button
                          onClick={() => openEdit(rule)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                          title={t('adminRevisions.editRule')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title={t('adminRevisions.deleteRule')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>{rule.erp_name ?? rule.erp_id}</span>
                      <span>·</span>
                      <span>{rule.interval_days}j</span>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      {[
                        t('adminRevisions.ruleErp'),
                        t('adminRevisions.ruleProduct'),
                        t('adminRevisions.ruleLabel'),
                        t('adminRevisions.ruleInterval'),
                        t('adminRevisions.ruleActive'),
                        '',
                      ].map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {rule.erp_name ?? rule.erp_id}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-800 dark:text-gray-200">
                          {rule.product_ref}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                          {rule.label}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {rule.interval_days}j
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${rule.is_active ? 'bg-green-500' : 'bg-gray-400'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => openEdit(rule)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                              title={t('adminRevisions.editRule')}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => handleDelete(rule)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                              title={t('adminRevisions.deleteRule')}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Schedules tab ── */}
      {tab === 'schedules' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
            >
              <option value="">{t('adminRevisions.filterAll')}</option>
              <option value="overdue">{t('revisions.statusOverdue')}</option>
              <option value="due">{t('revisions.statusDue')}</option>
              <option value="ok">{t('revisions.statusOk')}</option>
            </select>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
              {t('adminRevisions.syncBtn')}
            </button>
            {syncMsg && <span className="text-sm text-gray-600 dark:text-gray-400">{syncMsg}</span>}
          </div>

          {schedulesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Clock className="mx-auto h-10 w-10 mb-3 opacity-40" />
              <p>{t('adminRevisions.noSchedules')}</p>
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 bg-white dark:bg-gray-800 rounded-xl shadow sm:hidden">
                {schedules.map((s) => (
                  <li key={s.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                          {s.label}
                        </p>
                        <p className="text-xs font-mono text-gray-400 mt-0.5">{s.product_ref}</p>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                      <p className="truncate">
                        {s.customer_name ?? s.supplier_id} · {s.erp_name ?? ''}
                      </p>
                      {s.next_revision_at && (
                        <p>
                          {t('adminRevisions.colNextRevision')} : {formatDate(s.next_revision_at)}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      {[
                        t('adminRevisions.ruleErp'),
                        t('adminRevisions.colCustomer'),
                        t('revisions.colProduct'),
                        t('adminRevisions.colLastPurchase'),
                        t('adminRevisions.colNextRevision'),
                        t('adminRevisions.colStatus'),
                      ].map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {schedules.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {s.erp_name ?? ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                          {s.customer_name ?? s.supplier_id}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {s.label}
                          </div>
                          <div className="text-xs text-gray-400 font-mono">{s.product_ref}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {s.last_purchased_at ? formatDate(s.last_purchased_at) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {s.next_revision_at ? formatDate(s.next_revision_at) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={s.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Parc tab ── */}
      {tab === 'parc' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Status filter */}
              <select
                value={parcStatusFilter}
                onChange={(e) => setParcStatusFilter(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              >
                <option value="">{t('adminRevisions.filterAll')}</option>
                <option value="active">{t('parc.statusActive')}</option>
                <option value="end_of_life">{t('parc.statusEndOfLife')}</option>
                <option value="retired">{t('parc.statusRetired')}</option>
              </select>
              {/* Customer filter */}
              <select
                value={parcSupplierFilter}
                onChange={(e) => setParcSupplierFilter(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              >
                <option value="">
                  {t('adminRevisions.colCustomer')}: {t('adminRevisions.filterAll')}
                </option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.customer_name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={openAddEquip}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus size={16} />
              {t('parc.addEquipment')}
            </button>
          </div>

          {parcLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : parc.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Package className="mx-auto h-10 w-10 mb-3 opacity-40" />
              <p>{t('parc.noEquipment')}</p>
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 bg-white dark:bg-gray-800 rounded-xl shadow sm:hidden">
                {parc.map((item) => (
                  <li
                    key={item.id}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => openHistory(item)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                          {item.display_label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.customer_name ?? item.supplier_id}
                        </p>
                      </div>
                      <EquipmentStatusBadge item={item} />
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                        {item.serial_number && (
                          <p>
                            {t('parc.colSerial')}: {item.serial_number}
                          </p>
                        )}
                        {item.next_revision_at && (
                          <p>
                            {t('parc.colNextRevision')}: {formatDate(item.next_revision_at)}
                          </p>
                        )}
                      </div>
                      <div
                        className="flex items-center gap-1 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.status === 'active' && (
                          <button
                            onClick={() => openRevise(item)}
                            className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                            title={t('parc.revise')}
                          >
                            <CheckCircle size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => openEditEquip(item)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                          title={t('adminRevisions.editRule')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => handleDeleteEquip(item)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title={t('adminRevisions.deleteRule')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      {[
                        t('adminRevisions.ruleErp'),
                        t('adminRevisions.colCustomer'),
                        t('parc.colLabel'),
                        t('parc.colSerial'),
                        t('parc.colNextRevision'),
                        t('parc.colStatus'),
                        '',
                      ].map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {parc.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => openHistory(item)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {item.erp_name ?? ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                          {item.customer_name ?? item.supplier_id}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {item.display_label}
                          </div>
                          <div className="text-xs text-gray-400 font-mono">{item.product_ref}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {item.serial_number || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {item.next_revision_at ? formatDate(item.next_revision_at) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <EquipmentStatusBadge item={item} />
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            {item.status === 'active' && (
                              <button
                                onClick={() => openRevise(item)}
                                className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                                title={t('parc.revise')}
                              >
                                <CheckCircle size={15} />
                              </button>
                            )}
                            <button
                              onClick={() => openEditEquip(item)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                              title={t('adminRevisions.editRule')}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => handleDeleteEquip(item)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                              title={t('adminRevisions.deleteRule')}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Rule form modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editRule ? t('adminRevisions.editRule') : t('adminRevisions.addRule')}
            </h2>

            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                {formError}
              </div>
            )}

            <div className="space-y-3">
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('adminRevisions.ruleErp')}
                  </label>
                  <select
                    value={formData.erp_id ?? ''}
                    onChange={(e) => setFormData({ ...formData, erp_id: +e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    {erps.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.erp_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('adminRevisions.ruleProduct')}
                </label>
                <input
                  type="text"
                  value={formData.product_ref ?? ''}
                  onChange={(e) => setFormData({ ...formData, product_ref: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  placeholder="REF-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('adminRevisions.ruleLabel')}
                </label>
                <input
                  type="text"
                  value={formData.label ?? ''}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  placeholder="Révision annuelle extincteur"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('adminRevisions.ruleInterval')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.interval_days ?? 365}
                  onChange={(e) => setFormData({ ...formData, interval_days: +e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rule-active"
                  checked={!!formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="rule-active" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('adminRevisions.ruleActive')}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                onClick={handleSaveRule}
                disabled={formSaving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {formSaving ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  t('common.save', 'Enregistrer')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Equipment form modal ── */}
      {showEquipForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Package size={18} className="text-blue-600" />
                {editEquip ? t('parc.editEquipment') : t('parc.addEquipment')}
              </h2>
              <button
                onClick={() => setShowEquipForm(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {equipFormError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                  {equipFormError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('adminRevisions.ruleLabel')} *
                </label>
                <select
                  value={equipFormData.rule_id ?? ''}
                  onChange={(e) => setEquipFormData({ ...equipFormData, rule_id: +e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  <option value="">— {t('adminRevisions.ruleLabel')} —</option>
                  {rules
                    .filter((r) => r.is_active)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label} ({r.product_ref})
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('adminRevisions.colCustomer')} *
                </label>
                <select
                  value={equipFormData.supplier_id ?? ''}
                  onChange={(e) =>
                    setEquipFormData({ ...equipFormData, supplier_id: +e.target.value })
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  <option value="">— {t('adminRevisions.colCustomer')} —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.customer_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.colSerial')}
                </label>
                <input
                  type="text"
                  value={equipFormData.serial_number ?? ''}
                  onChange={(e) =>
                    setEquipFormData({ ...equipFormData, serial_number: e.target.value })
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.colPurchase')}
                </label>
                <input
                  type="date"
                  value={equipFormData.purchase_date ?? ''}
                  onChange={(e) =>
                    setEquipFormData({ ...equipFormData, purchase_date: e.target.value })
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
              {editEquip && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('parc.colStatus')}
                  </label>
                  <select
                    value={equipFormData.status ?? 'active'}
                    onChange={(e) =>
                      setEquipFormData({
                        ...equipFormData,
                        status: e.target.value as EquipmentItem['status'],
                      })
                    }
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    <option value="active">{t('parc.statusActive')}</option>
                    <option value="end_of_life">{t('parc.statusEndOfLife')}</option>
                    <option value="retired">{t('parc.statusRetired')}</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.revisionNotes')}
                </label>
                <textarea
                  value={equipFormData.notes ?? ''}
                  onChange={(e) => setEquipFormData({ ...equipFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowEquipForm(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  onClick={handleSaveEquip}
                  disabled={equipFormSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {equipFormSaving ? (
                    <RefreshCw size={15} className="animate-spin" />
                  ) : (
                    t('common.save', 'Enregistrer')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin revise modal ── */}
      {reviseItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('parc.revise')}
              </h2>
              <button
                onClick={() => setReviseItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleRevise} className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {reviseItem.display_label} — {reviseItem.customer_name}
              </p>
              {reviseError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                  {reviseError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.revisionDate')} *
                </label>
                <input
                  type="date"
                  value={reviseDate}
                  onChange={(e) => setReviseDate(e.target.value)}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.revisionNotes')}
                </label>
                <textarea
                  value={reviseNotes}
                  onChange={(e) => setReviseNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setReviseItem(null)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  disabled={reviseSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {reviseSaving && <RefreshCw size={14} className="animate-spin" />}
                  {t('common.save', 'Enregistrer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── History modal ── */}
      {historyItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <History size={18} className="text-blue-600" />
                {t('parc.history')}
              </h2>
              <button
                onClick={() => setHistoryItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {historyItem.display_label}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {historyItem.customer_name}
              </p>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('parc.noHistory')}
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {history.map((rev) => (
                    <div
                      key={rev.id}
                      className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                    >
                      <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(rev.revised_at)}
                        </div>
                        {rev.notes && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {rev.notes}
                          </div>
                        )}
                        {(rev.firstname || rev.lastname) && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {[rev.firstname, rev.lastname].filter(Boolean).join(' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
